import { useState } from "react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CaretDownIcon, CheckCircleIcon, MagnifyingGlassIcon, XIcon } from "@phosphor-icons/react";

/*
  To / Cc / Bcc for sending a digest or a report.

  Each field draws from a DIFFERENT pool, because the three lines mean different
  things:

    To   the owners, plus the manager of the project in question — the people
         the thing is addressed to. Picked from a list, not typed: "only owners
         should be in To" is a rule, and a free-text box is not a rule.
    Cc   all owners plus that project's members — the people who should see it.
         Also picked from a list.
    Bcc  anyone, typed or picked. A blind copy is for whoever needs a quiet
         copy, so it is not constrained.

  A field whose pool is empty says so instead of rendering a dead dropdown, and
  an address already in the value is always shown even if it is not in the pool
  — a saved recipient must never silently disappear.
*/

const EMAIL = /^\S+@\S+\.\S+$/;

/** The pool as {email, label, hint}, minus anyone already chosen. */
function optionsFrom(pool, chosen) {
  return (pool || [])
    .map((u) => ({
      email: (u.email || "").trim(),
      label: u.name || u.email || "Unknown",
      hint: u.role || "",
    }))
    .filter((o) => o.email && !chosen.includes(o.email));
}

function PickFromList({ pool, chosen, onPick, testId, label }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const options = optionsFrom(pool, chosen);
  const q = query.trim().toLowerCase();
  const matches = q
    ? options.filter((o) => `${o.label} ${o.email}`.toLowerCase().includes(q))
    : options;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={`${testId}-pick`}
          className="flex h-9 items-center justify-between gap-2 rounded-sm border border-input bg-white px-3 text-sm shadow-sm transition-colors hover:bg-zinc-50 min-w-[190px]"
        >
          <span className="truncate text-muted-foreground">{label}</span>
          <CaretDownIcon size={12} className="shrink-0 text-zinc-500" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        {pool.length > 4 && (
          <div className="relative border-b border-border p-2">
            <MagnifyingGlassIcon size={13} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              data-testid={`${testId}-search`}
              className="h-8 rounded-sm pl-7 text-sm"
            />
          </div>
        )}
        <div className="max-h-56 overflow-y-auto divide-y divide-border">
          {options.length === 0 && (
            <div className="px-3 py-3 text-xs text-zinc-500">Everyone available is already added.</div>
          )}
          {options.length > 0 && matches.length === 0 && (
            <div className="px-3 py-3 text-xs text-zinc-500">Nobody matches “{query.trim()}”.</div>
          )}
          {matches.map((o) => (
            <button
              type="button"
              key={o.email}
              onClick={() => { onPick(o.email); setQuery(""); }}
              data-testid={`${testId}-option-${o.email}`}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-zinc-50"
            >
              <span className="min-w-0">
                <span className="truncate">{o.label}</span>
                {o.hint && (
                  <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-400 ml-1.5">{o.hint}</span>
                )}
                <span className="block text-[10px] font-mono text-zinc-400 truncate">{o.email}</span>
              </span>
              <CheckCircleIcon size={14} className="shrink-0 text-zinc-300" />
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Row({ label, hint, field, value, onChange, pool, allowFree, addAllLabel, testId }) {
  const [draft, setDraft] = useState("");

  const add = (raw) => {
    const val = (raw ?? draft).trim().replace(/,$/, "");
    if (!val || !EMAIL.test(val)) return;
    if (value.includes(val)) { setDraft(""); return; }
    onChange([...value, val]);
    setDraft("");
  };

  const poolEmails = (pool || []).map((u) => (u.email || "").trim()).filter(Boolean);

  return (
    <div className="mb-3">
      <div className="flex items-center justify-between mb-1">
        <span className="trk-uppercase-label">{label} {value.length > 0 && `(${value.length})`}</span>
        <span className="text-[10px] font-mono text-zinc-400">{hint}</span>
      </div>

      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-1.5">
          {value.map((r) => (
            <span key={r} className="inline-flex items-center gap-1 px-2 py-0.5 text-xs bg-white border border-border rounded-sm">
              {r}
              <button
                type="button"
                onClick={() => onChange(value.filter((x) => x !== r))}
                data-testid={`${testId}-${field}-remove-${r}`}
                className="hover:text-red-600"
              >
                <XIcon size={10} weight="bold" />
              </button>
            </span>
          ))}
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        {poolEmails.length > 0 ? (
          <>
            <PickFromList
              pool={pool}
              chosen={value}
              onPick={(email) => add(email)}
              testId={`${testId}-${field}`}
              label={`Add ${label.toLowerCase()}…`}
            />
            {poolEmails.some((e) => !value.includes(e)) && (
              <Button
                variant="outline"
                onClick={() => onChange(Array.from(new Set([...value, ...poolEmails])))}
                data-testid={`${testId}-${field}-all`}
                className="rounded-sm h-9"
              >
                {addAllLabel} ({poolEmails.length})
              </Button>
            )}
          </>
        ) : (
          !allowFree && (
            <span className="text-[11px] text-zinc-500" data-testid={`${testId}-${field}-empty`}>
              Nobody eligible for this field yet.
            </span>
          )
        )}

        {allowFree && (
          <div className="flex items-center gap-2 flex-1 min-w-[220px]">
            <Input
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } }}
              // Committed on blur too: clicking Send straight after typing is
              // the obvious move, and losing the address there is what made the
              // old single-field version look broken.
              onBlur={() => add()}
              placeholder="email@company.com"
              data-testid={`${testId}-${field}-input`}
              className="rounded-sm h-9"
            />
            <Button variant="outline" onClick={() => add()} className="rounded-sm h-9" data-testid={`${testId}-${field}-add`}>
              Add
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default function RecipientFields({ value, onChange, pools = {}, testId = "recipients" }) {
  const to = value?.to || [];
  const cc = value?.cc || [];
  const bcc = value?.bcc || [];

  // Open when it already holds something, so nothing is hidden.
  const [showCc, setShowCc] = useState(cc.length > 0);
  const [showBcc, setShowBcc] = useState(bcc.length > 0);

  const set = (patch) => onChange({ to, cc, bcc, ...patch });

  return (
    <div>
      <Row
        label="To" hint="owners + the project's manager" field="to"
        value={to} onChange={(next) => set({ to: next })}
        pool={pools.to || []} allowFree={false} addAllLabel="All owners"
        testId={testId}
      />

      {showCc && (
        <Row
          label="Cc" hint="owners + the project's members, or any address" field="cc"
          value={cc} onChange={(next) => set({ cc: next })}
          // Typed addresses allowed here, unlike To: "only owners in To" is the
          // rule, but a copy legitimately goes to people who have no account —
          // an external stakeholder, or a test mailbox.
          pool={pools.cc || []} allowFree addAllLabel="Add all"
          testId={testId}
        />
      )}

      {showBcc && (
        <Row
          label="Bcc" hint="anyone — hidden from the others" field="bcc"
          value={bcc} onChange={(next) => set({ bcc: next })}
          pool={pools.bcc || []} allowFree addAllLabel="Whole team"
          testId={testId}
        />
      )}

      <div className="flex items-center gap-3">
        {!showCc && (
          <button type="button" onClick={() => setShowCc(true)} data-testid={`${testId}-show-cc`}
                  className="text-[10px] font-mono uppercase tracking-wider text-[#0176D3] hover:underline">
            + Add Cc
          </button>
        )}
        {!showBcc && (
          <button type="button" onClick={() => setShowBcc(true)} data-testid={`${testId}-show-bcc`}
                  className="text-[10px] font-mono uppercase tracking-wider text-[#0176D3] hover:underline">
            + Add Bcc
          </button>
        )}
      </div>
    </div>
  );
}

/** Flattens the three fields for a count or a confirmation message. */
export const allRecipients = (value) => [
  ...new Set([...(value?.to || []), ...(value?.cc || []), ...(value?.bcc || [])]),
];
